import dayjs from 'dayjs';
import {
  DATE_TEMPLATE_BY_DAY_LONG,
  DATE_TEMPLATE_BY_MONTH_SHORT,
  DAYJS_ISO_DATE_FORMAT,
} from 'core/constants/date.constants';
import { TIME_24H_REGEX } from 'core/constants/regex.constant';

export const LOCALE_TIMEZONE = 'America/Lima' as const;

/**
 * It will format a date to a string in the format 'YYYY-MM-DD' or a custom format if provided.
 */
export const formatDate = (date: dayjs.ConfigType, template?: string) =>
  dayjs(date)
    .locale('es')
    .format(template || 'YYYY-MM-DD');
export const createDate = (date: string | undefined) => (date ? dayjs(date).toDate() : dayjs().toDate());
/**
 * It formats a date to a Peruvian locale date string in the format 'YYYY-MM-DD'.
 *
 * @param isoDate - An ISO date string.
 */
export const formatLocaleDate = (isoDate: string, format = 'YYYY-MM-DD') =>
  dayjs(isoDate).tz(LOCALE_TIMEZONE).locale('es').format(format);
/**
 * It formats a date to a Peruvian locale time string in the format 'HH:mm'.
 *
 * @param date - An ISO date string.
 *
 * @example
 * formatLocaleTime('2023-10-05T14:30:00Z'); // '14:30'
 */
export const formatLocaleTime = (isoDate: string, template?: string) =>
  dayjs(isoDate)
    .tz(LOCALE_TIMEZONE)
    .locale('es')
    .format(template || 'HH:mm');
/**
 * ### Local Date and Time
 *
 * @example
 * toDateTimeByIsoDateAndTime('2023-10-05', '14:30');
 */
export const toDateTimeByIsoDateAndTime = (isoDate: string, time: string) => {
  const date = formatLocaleDate(isoDate);

  const dateTime = `${date}T${time}`;

  const value = dayjs(dateTime).tz(LOCALE_TIMEZONE);

  return value;
};
/**
 * It formats a date to a Peruvian locale date string in the format 'YYYY-MM-DDTHH:mm:ssZ'.
 *
 * @param isoDate - An ISO date string.
 *
 * @example
 * formatLocaleTime('2025-09-04T00:00:00Z'); // '2025-09-04T00:00:00-05:00'
 */
export const formatLocaleDateTime = (isoDate: string) =>
  dayjs(isoDate).tz(LOCALE_TIMEZONE).locale('es').format('YYYY-MM-DDTHH:mm:ssZ');
/**
 * It formats a date to a Peruvian locale time string in the format 'hh:mm A'.
 *
 * @param date - An ISO date string.
 *
 * @example
 * formatLocaleTime('2023-10-05T14:30:00Z'); // '02:30 PM'
 */
export const formatLocale12H = (isoDate: string) => dayjs(isoDate).tz(LOCALE_TIMEZONE).locale('es').format('hh:mm A');

/**
 * Formats a 24-hour time string (HH:mm) to a 24-hour format with an AM/PM suffix.
 *
 * @example
 * // returns '15:45 PM'
 * formatTime24h('15:45');
 *
 * @example
 * // returns '09:30 AM'
 * formatTime24h('09:30');
 *
 * @example
 * // returns '00:00 AM'
 * formatTime24h('00:00');
 * * @example
 * // returns '12:00 PM'
 * formatTime24h('12:00');
 *
 * @param time - The time string in HH:mm format.
 * @returns The formatted time string (e.g., '15:45 PM') or an error message for invalid input.
 */
export const formatTime24hMeridiem = (time: string): string => {
  /**
   * Basic validation for the HH:mm format
   */
  const isInvalid = !TIME_24H_REGEX.test(time);

  if (isInvalid) {
    return 'Invalid time format. Please use HH:mm.';
  }

  /**
   * Split the string to get the hour part
   */
  const [hourString] = time.split(':');

  const hour = Number.parseInt(hourString, 10);

  /**
   * Check if the parsed hour is a valid number (0-23)
   */
  const isHourInvalid = Number.isNaN(hour) || hour < 0 || hour > 23;

  if (isHourInvalid) {
    return 'Invalid hour in time string.';
  }

  /**
   * Determine if the time is in the AM or PM period.
   */
  const period = hour >= 12 ? 'PM' : 'AM';

  /**
   * Return the formatted time string with the period (AM/PM).
   */
  const result = `${time} ${period}`;

  return result;
};
/**
 * It formats a date to a Peruvian locale date string in the format 'DD de MMMM'.
 *
 * @param date - An ISO date string.
 *
 * This function capitalizes the first letter of the month.
 * For example, '2023-10-05' will be formatted as '05 de Octubre'.
 */
export const formatDateToDayAndLongMonth = (date: string) => {
  const formattedDate = formatDate(date, DATE_TEMPLATE_BY_DAY_LONG);

  const capitalized = formattedDate.replace(
    /de (\w+)/,
    (_, month) => `de ${month.charAt(0).toUpperCase()}${month.slice(1)}`,
  );

  return capitalized;
};

/**
 * It formats a date to a Peruvian locale date string in the format 'DD de MM'.
 *
 * @param date - An ISO date string.
 *
 * This function capitalizes the first letter of the month.
 * For example, '2023-10-05' will be formatted as '05 Oct'.
 */
export const formatDateToDayAndShortMonth = (date: string) => {
  const formattedDate = formatDate(date, DATE_TEMPLATE_BY_MONTH_SHORT);

  const capitalized = formattedDate.replace(
    /de (\w+)/,
    (_, month: string) => `${month.charAt(0).toLowerCase()}${month.slice(1)}`,
  );

  return capitalized;
};

export const resetTimeToStartOfDay = (date: Date) => {
  date.setHours(0);

  date.setMinutes(0);

  date.setSeconds(0);

  date.setMilliseconds(0);

  return date;
};

/**
 * Formats a date and time to an ISO 8601 string.
 *
 * It takes a Date object and a time string in 'HH:mm' format.
 *
 * Resets the time of the date to the start of the day (00:00) before combining it with the provided time.
 */
export const formatDateTimeToIso = (date: Date, time: string) => {
  resetTimeToStartOfDay(date);

  const formattedDate = dayjs.utc(date).format(DAYJS_ISO_DATE_FORMAT);

  const datetime = dayjs.tz(`${formattedDate}T${time}`, LOCALE_TIMEZONE);

  const isoDate = datetime.toISOString();

  return isoDate;
};

export const isDateTimeAfter = (startDate: Date, startTime: string, endDate: Date, endTime: string) => {
  const isSameDay = dayjs(startDate).isSame(endDate, 'day');

  if (isSameDay) {
    const startDateTime = formatDateTimeToIso(startDate, startTime);

    const endDateTime = formatDateTimeToIso(endDate, endTime);

    const isAfter = dayjs(endDateTime).isAfter(startDateTime);

    return isAfter;
  }

  return true;
};

export const isDateTimeInFutureWhenIsToday = (date: Date, time: string) => {
  const now = dayjs();

  const isToday = dayjs(date).isSame(now, 'day');

  if (isToday) {
    const scheduledDateTime = formatDateTimeToIso(date, time);

    const isInFuture = dayjs(scheduledDateTime).isAfter(now);

    return isInFuture;
  }

  return true;
};

export const isDateTimeInFuture = (date: Date, time: string) => {
  const scheduledDateTime = formatDateTimeToIso(date, time);

  const now = dayjs();

  const isInFuture = dayjs(scheduledDateTime).isAfter(now);

  return isInFuture;
};

export const isDateTimeInPast = (date: Date, time: string) => {
  const scheduledDateTime = formatDateTimeToIso(date, time);

  const now = dayjs();

  const isInPast = dayjs(scheduledDateTime).isBefore(now);

  return isInPast;
};

export const isDateTimeToday = (date: Date, time: string) => {
  if (!date) {
    return false;
  }

  const now = dayjs();

  const scheduledDateTime = formatDateTimeToIso(date, time);

  const isSameDay = dayjs(scheduledDateTime).isSame(now, 'day');

  return isSameDay;
};

export const isEndDateTimeExceedsOneMonth = (
  startDate: Date,
  startTime: string,
  endDate: Date,
  endTime: string,
): boolean => {
  if (!startDate || !startTime || !endDate || !endTime) {
    return false;
  }

  const startDateTime = formatDateTimeToIso(startDate, startTime);
  const endDateTime = formatDateTimeToIso(endDate, endTime);

  const maxAllowedEndDateTime = dayjs(startDateTime).add(1, 'month');

  return dayjs(endDateTime).isAfter(maxAllowedEndDateTime);
};

/**
 * Formats a Dayjs date using the given template and capitalizes
 * the month abbreviation. Defaults to "DD MMM YYYY".
 *
 * Example:
 *   formatDateWithCapitalizedMonth(dayjs("2025-05-12"))
 *   → "12 May"
 *
 *   formatDateWithCapitalizedMonth(dayjs("2025-05-12"), "DD MMM YYYY")
 *   → "12 May 2025"
 */
export const formatDateWithCapitalizedMonth = (date: string | Date, template = 'DD MMM YYYY') =>
  formatDate(date, template).replace(/\b\w/g, (c: string) => c.toUpperCase());
